/**
 * Strip the trailing "-<writing_agent_id>" suffix that the Messer master xlsx
 * concatenates onto the policy_id column. Does nothing when the suffix
 * doesn't match (newer raw-CSV imports don't have this issue).
 */
export function cleanPolicyId(
  policyId: string | undefined | null,
  writingId: string | undefined | null,
): string {
  if (!policyId) return '';
  if (!writingId) return policyId;
  const suffix = `-${writingId}`;
  return policyId.endsWith(suffix) ? policyId.slice(0, -suffix.length) : policyId;
}

/**
 * Proper-case a single name token. Tokens that ALREADY contain a mix of upper
 * and lower case are preserved as-is, so intentional internal capitals survive
 * (e.g. "McCorkle", "O'Brien", "DeLuca"). All-caps or all-lowercase tokens get
 * title-cased ("RICHARD" -> "Richard"), handling hyphen/apostrophe sub-parts
 * ("sheri-ann" -> "Sheri-Ann").
 */
function properCaseToken(t: string): string {
  if (!t) return t;
  if (/[a-z]/.test(t) && /[A-Z]/.test(t)) return t; // already mixed — leave it
  return t.toLowerCase().replace(/(^|[-'])([a-z])/g, (_, p, c) => p + c.toUpperCase());
}

/** Proper-case every whitespace-separated token in a display name. */
export function properName(name: string | undefined | null): string {
  if (!name) return '';
  return name.trim().split(/\s+/).map(properCaseToken).join(' ');
}

/**
 * Per-system house check (mirrors the backend). An agent is "house" for a
 * system when that system isn't in their `paid_systems`. Replaces the legacy
 * global `payout === false` check.
 */
export function isHouseForSystem(
  agent: { paid_systems?: string[] | null } | undefined | null,
  sys: string | null | undefined,
): boolean {
  if (!sys) return false;
  return !(agent?.paid_systems?.includes(sys));
}

/**
 * Convert an agent's stored "Last, First" name into a friendly, proper-cased
 * "First Last" for agent-facing greetings (e.g. "Smith, RICHARD F" ->
 * "Richard F Smith", "Villacis, Gabriel" -> "Gabriel Villacis"). Names already
 * in "First Last" form (no comma) are proper-cased and returned.
 */
export function friendlyName(name: string | undefined | null): string {
  if (!name) return '';
  const i = name.indexOf(',');
  const reordered = i === -1 ? name.trim() : `${name.slice(i + 1).trim()} ${name.slice(0, i).trim()}`;
  return properName(reordered);
}
